/**
 * SectionPanel — Left panel.
 * Lists all sections as sortable thumbnails; has add-section button with grouped menu.
 */
import React from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Type, Image, Minus, Columns, Zap, Megaphone,
  GripVertical, Plus,
  LayoutTemplate, Quote, UserCircle, Heart, Briefcase, PanelBottom,
} from 'lucide-react';
import { useEditorStore } from '../stores/editorStore';

// lucide-react has no SpaceIcon — use a simple inline SVG
const SpaceIcon = ({ size = 14, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="12" x2="20" y2="12" />
    <polyline points="8 8 4 12 8 16" />
    <polyline points="16 8 20 12 16 16" />
  </svg>
);


const SECTION_TYPES = [
  // ── Structure ────────────────────────────────────────────────
  { type: 'header', label: 'Header', Icon: LayoutTemplate, group: 'Structure' },
  { type: 'footer', label: 'Footer', Icon: PanelBottom, group: 'Structure' },
  { type: 'divider', label: 'Divider', Icon: Minus, group: 'Structure' },
  { type: 'spacer', label: 'Spacer', Icon: SpaceIcon, group: 'Structure' },
  // ── Content ──────────────────────────────────────────────────
  { type: 'text', label: 'Text Block', Icon: Type, group: 'Content' },
  { type: 'image', label: 'Image', Icon: Image, group: 'Content' },
  { type: 'hero', label: 'Hero Banner', Icon: Zap, group: 'Content' },
  { type: 'cta', label: 'CTA Button', Icon: Megaphone, group: 'Content' },
  { type: 'columns', label: 'Two Columns', Icon: Columns, group: 'Content' },
  // ── Editorial ────────────────────────────────────────────────
  { type: 'founders_focus', label: "Founder's Focus", Icon: Quote, group: 'Editorial' },
  { type: 'meet_engineer', label: 'Meet the Engineer', Icon: UserCircle, group: 'Editorial' },
  { type: 'appreciation', label: 'Appreciation', Icon: Heart, group: 'Editorial' },
  { type: 'project_update', label: 'Project Update', Icon: Briefcase, group: 'Editorial' },
];

const GROUPS = ['Structure', 'Content', 'Editorial'];

// Human-readable labels for the thumbnail list
const TYPE_LABELS = {
  header: 'Header',
  footer: 'Footer',
  divider: 'Divider',
  spacer: 'Spacer',
  text: 'Text',
  image: 'Image',
  hero: 'Hero',
  cta: 'CTA',
  columns: 'Two Columns',
  founders_focus: "Founder's Focus",
  meet_engineer: 'Meet the Engineer',
  appreciation: 'Appreciation',
  project_update: 'Project Update',
};

function SectionThumb({ section, isSelected }) {
  const { selectSection } = useEditorStore();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style, display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
        background: isSelected ? '#fff7f5' : '#fff',
        border: isSelected ? '1.5px solid #f54b01' : '1.5px solid transparent',
        marginBottom: 4,
      }}
      onClick={() => selectSection(section.id)}
    >
      <button
        {...attributes} {...listeners}
        style={{ background: 'none', border: 'none', cursor: 'grab', color: '#9ca3af', padding: 0, display: 'flex' }}
      >
        <GripVertical size={14} />
      </button>
      <span style={{
        fontSize: 11, fontWeight: 500, flex: 1,
        color: isSelected ? '#f54b01' : '#374151',
      }}>
        {TYPE_LABELS[section.type] || section.type || 'Section'}
      </span>
      <span style={{ fontSize: 10, color: '#9ca3af' }}>
        #{section.id.slice(0, 4)}
      </span>
    </div>
  );
}

export function SectionPanel() {
  const { sections, selectedSectionId, addSection, reorderSections } = useEditorStore();
  const [showAddMenu, setShowAddMenu] = React.useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    reorderSections(arrayMove(sections, oldIdx, newIdx));
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--em-border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>
            Sections ({sections.length})
          </h3>
          <button
            onClick={() => setShowAddMenu((v) => !v)}
            style={{
              background: '#f54b01', color: '#fff', border: 'none',
              borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
              fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            <Plus size={13} /> Add
          </button>
        </div>

        {/* Add section menu — grouped */}
        {showAddMenu && (
          <>
            {/* Backdrop */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 49 }}
              onClick={() => setShowAddMenu(false)}
            />
            <div style={{
              position: 'absolute', left: 12, right: 12, zIndex: 50,
              marginTop: 8, background: 'var(--em-bg-card)', border: '1px solid var(--em-border)',
              borderRadius: 10, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              maxHeight: 420, overflowY: 'auto',
            }}>
              {GROUPS.map((group) => {
                const items = SECTION_TYPES.filter((t) => t.group === group);
                return (
                  <div key={group}>
                    <div style={{
                      padding: '8px 12px 4px', fontSize: 10, fontWeight: 700,
                      color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1,
                      borderTop: group !== 'Structure' ? '1px solid #f3f4f6' : 'none',
                      background: '#fafafa',
                    }}>
                      {group}
                    </div>
                    {items.map(({ type, label, Icon }) => (
                      <button
                        key={type}
                        onClick={() => { addSection(type, selectedSectionId); setShowAddMenu(false); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                          padding: '9px 14px', background: 'none', border: 'none',
                          cursor: 'pointer', fontSize: 13, color: '#374151', textAlign: 'left',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                      >
                        <Icon size={14} color="#f54b01" />
                        {label}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Section list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
        {sections.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, paddingTop: 32 }}>
            <Type size={32} color="#e5e7eb" style={{ marginBottom: 8 }} />
            <p>No sections yet.<br />Click Add to start.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {sections.map((section) => (
                <SectionThumb
                  key={section.id}
                  section={section}
                  isSelected={section.id === selectedSectionId}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
